import { useCallback, useDeferredValue, useEffect, useMemo, useRef, useState, useSyncExternalStore, type ReactNode } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useEnv } from '../config/env';
import { outcomeMid, useAllMids } from '../hl/mids';
import { applyFilter, isSortKey, toItem, venueCounts, type SortKey } from '../catalog/catalog';
import CatalogControls from '../catalog/CatalogControls';
import TagChips from '../catalog/TagChips';
import { chipRow, classifyMeta, effectiveSelection, loadStoredTags, parseTagList, rulesStore, saveStoredTags } from '../classify';
import CompactList from '../catalog/CompactList';
import InlineMarket from '../catalog/InlineMarket';
import { PANEL_PARAM, panelSlot } from '../catalog/layout';
import { MARKETS_TOP_HEIGHT, useCatalogLayout, useCatalogView, useMarketsFill, useMarketsMiniPortfolio } from '../catalog/prefs';
import MiniPortfolio from '../catalog/MiniPortfolio';
import { useViewedAccount } from '../wallet/context';
import OutcomeTile from '../catalog/OutcomeTile';
import QuestionCard from '../catalog/QuestionCard';
import { toEntries } from '../catalog/group';
import { useDescriber } from '../describe/useDescribe';
import { useMediaQuery } from '../catalog/useMediaQuery';
import { useOutcomeMeta } from '../catalog/useOutcomeMeta';
import { parseOutcomeId } from '../market/outcomeId';
import { scopeOutcomes, scopeQuestions, venueIndex, venueLabel } from '../venue/scope';
import { useVenueScope } from '../venue/useVenueScope';
import Msg from '../i18n/Msg';
import { networkWord } from '../i18n/common';
import { useT } from '../i18n/useT';
import { useDayVolumes } from '../volume/useVolume';

/** Tiles rendered per "Show more" step: keeps the first paint cheap on phones with ~700 outcomes. */
const PAGE_SIZE = 60;
const SEP = '\n';
/** Catalog venue-chip filter param. (`?venue=` is the app-wide venue-scope override, #689 / docs/VENUES.md.) */
const VF = 'vf';
/** Market-tag filter param (#875): comma-separated tag ids; also remembered per device (classify/ loadStoredTags). */
const TAG = 'tag';

/** Tailwind's md, lg and xl: the tile grid's columns, and where the compact list has room for a panel beside it. */
const MD = '(min-width: 768px)';
const LG = '(min-width: 1024px)';
const XL = '(min-width: 1280px)';

/** History state of an entry that opened the panel: the filters it was opened over (the search without the panel). */
interface PanelEntry {
  panelBase: string;
}

function baseSearch(params: URLSearchParams): string {
  const next = new URLSearchParams(params);
  next.delete(PANEL_PARAM);
  return next.toString();
}

export default function MarketsPage() {
  const { env } = useEnv();
  const t = useT();
  const network = networkWord(env.name);
  const [params, setParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const meta = useOutcomeMeta();
  const { mids } = useAllMids();
  // #874: one spotMetaAndAssetCtxs request gives the 24 h volume of every outcome (no per-row requests).
  const { volumes } = useDayVolumes();
  const { venue: scopeVenue } = useVenueScope();

  // Filter state lives in the URL (?q=&vf=&sort=) so Back from a market restores it.
  const query = params.get('q') ?? '';
  const sortParam = params.get('sort');
  const sort: SortKey = isSortKey(sortParam) ? sortParam : 'id';
  // Venue chips are hidden while the app is scoped to one venue (#689), so their filter is ignored then.
  const venueKeyStr = scopeVenue === null ? params.getAll(VF).join(SEP) : '';
  const selected = useMemo(() => new Set(venueKeyStr ? venueKeyStr.split(SEP) : []), [venueKeyStr]);
  // Grouped (question cards, default) or flat tiles (#690): set in the header Settings menu (#2242), ?view= wins.
  const [view] = useCatalogView();
  // Market tags (#875): ?tag=a,b wins, else the selection remembered on this device.
  const [storedTags, setStoredTags] = useState<string[]>(() => loadStoredTags());
  const tagParam = params.get(TAG);
  const requestedTags = useMemo(() => (tagParam !== null ? parseTagList(tagParam) : storedTags), [tagParam, storedTags]);
  // Rules: bundled, or the downloaded copy (checked at most hourly when the Markets list opens).
  const rules = useSyncExternalStore(rulesStore.subscribe, rulesStore.getSnapshot);
  useEffect(() => {
    void rulesStore.refreshIfDue();
  }, []);
  // Tiles or compact rows (#1953), remembered on this device; set in the header Settings menu (#2242).
  const [layout] = useCatalogLayout();
  const compact = layout === 'compact';
  const mdUp = useMediaQuery(MD);
  const lgUp = useMediaQuery(LG);
  const xlUp = useMediaQuery(XL);
  const tileCols = xlUp ? 3 : mdUp ? 2 : 1;
  const ghost = params.get('ghost');
  const linkSearch = ghost ? `?${new URLSearchParams({ ghost }).toString()}` : '';

  const update = (fn: (p: URLSearchParams) => void) =>
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        fn(next);
        return next;
      },
      // Keeps the panel's history state: closing it still knows whether it can step back.
      { replace: true, state: location.state as unknown },
    );
  const setQuery = (q: string) => update((p) => (q ? p.set('q', q) : p.delete('q')));
  const setSort = (s: SortKey) => update((p) => (s === 'id' ? p.delete('sort') : p.set('sort', s)));
  const setVenues = (vs: Iterable<string>) =>
    update((p) => {
      p.delete(VF);
      for (const v of vs) p.append(VF, v);
    });
  const toggleVenue = (v: string) => {
    const next = new Set(selected);
    if (next.has(v)) next.delete(v);
    else next.add(v);
    setVenues(next);
  };
  const setTags = (next: string[]) => {
    saveStoredTags(next);
    setStoredTags(next);
    update((p) => (next.length > 0 ? p.set(TAG, next.join(',')) : p.delete(TAG)));
  };
  const clearFilters = () => {
    saveStoredTags([]);
    setStoredTags([]);
    update((p) => {
      p.delete('q');
      p.delete(VF);
      p.delete(TAG);
    });
  };

  const outcomes = meta.data?.outcomes;
  const describe = useDescriber(meta.data);
  const tags = useMemo(() => classifyMeta(meta.data, env.name, rules), [meta.data, env.name, rules]);
  const items = useMemo(
    () =>
      scopeOutcomes(outcomes ?? [], scopeVenue).map((o) =>
        toItem(o, outcomeMid(mids, o.outcome, 0), describe.outcome(o), volumes?.get(o.outcome), tags.outcome.get(o.outcome) ?? [], rules),
      ),
    [outcomes, mids, scopeVenue, describe, volumes, tags, rules],
  );
  // Tag chips count the outcomes in the venue scope (not the search / venue chips), so they do not jump while typing.
  const tagItems = useMemo(() => items.map((it) => it.tags), [items]);
  const selTags = useMemo(() => effectiveSelection(rules, tagItems, requestedTags), [rules, tagItems, requestedTags]);
  const row = useMemo(() => chipRow(rules, tagItems, selTags), [rules, tagItems, selTags]);
  const venues = useMemo(() => (scopeVenue === null ? venueCounts(items) : []), [items, scopeVenue]);
  const deferredQuery = useDeferredValue(query);
  const filtered = useMemo(
    () => applyFilter(items, { query: deferredQuery, venues: selected, sort, tags: selTags, rules }),
    [items, deferredQuery, selected, sort, selTags, rules],
  );

  const questions = meta.data?.questions;
  const entries = useMemo(
    () =>
      toEntries(filtered, view, {
        questions: scopeQuestions(questions ?? [], venueIndex(outcomes), scopeVenue),
        describeQuestion: describe.question,
        describeOutcome: describe.byId,
        dayVolumes: volumes,
        questionTags: (q) => tags.question.get(q.question) ?? [],
        rules,
      }),
    [filtered, view, questions, outcomes, scopeVenue, describe, volumes, tags, rules],
  );
  const questionCount = entries.filter((e) => e.kind === 'question').length;

  // Pagination resets whenever the filter, view or env changes.
  const filterKey = [env.name, scopeVenue, deferredQuery, venueKeyStr, sort, view, selTags.join(',')].join(SEP);
  const [page, setPage] = useState({ key: filterKey, count: PAGE_SIZE });
  const count = page.key === filterKey ? page.count : PAGE_SIZE;
  const visible = entries.slice(0, count);
  const filtering = query !== '' || selected.size > 0 || selTags.length > 0;

  // Inline market panel (#1953): ?m=<outcome>, so the outcome survives a reload and comes back after the full page.
  const openRaw = params.get(PANEL_PARAM);
  const openId = parseOutcomeId(openRaw ?? undefined);
  const closePanel = () => {
    const id = openId;
    const entry = location.state as Partial<PanelEntry> | null;
    // Same filters as when it opened: step back, so the entry that opened it does not stay behind as a dead Back press.
    if (entry?.panelBase === baseSearch(params)) navigate(-1);
    else update((p) => p.delete(PANEL_PARAM));
    if (id !== null) document.querySelector<HTMLElement>(`[data-outcome-link="${id}"]`)?.focus();
  };
  const selectOutcome = (id: number) => {
    if (id === openId) {
      closePanel();
      return;
    }
    // The first outcome pushes an entry, so Back closes the panel. Moving to another one replaces it: Back still closes
    // the panel rather than walking through everything looked at.
    const state: PanelEntry | unknown = openId === null ? ({ panelBase: baseSearch(params) } satisfies PanelEntry) : location.state;
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set(PANEL_PARAM, String(id));
        return next;
      },
      { replace: openId !== null, state },
    );
  };
  // Latest handlers behind stable functions: tiles are memoised, and the Escape listener outlives a render.
  const handlers = useRef({ closePanel, selectOutcome });
  handlers.current = { closePanel, selectOutcome };
  const openPanel = useCallback((id: number) => handlers.current.selectOutcome(id), []);
  const sectionRef = useRef<HTMLElement>(null);
  const panelOpen = openId !== null;
  useEffect(() => {
    if (!panelOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || e.defaultPrevented) return;
      // Escape belongs to an open dialog, a menu in the header, or the field being edited.
      if (document.querySelector('[aria-modal="true"]')) return;
      const el = e.target instanceof HTMLElement ? e.target : null;
      if (el && el !== document.body && !sectionRef.current?.contains(el)) return;
      if (el?.closest('input, select, textarea')) return;
      handlers.current.closePanel();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [panelOpen]);

  const beside = compact && lgUp;
  // #2242: compact on a desktop: one screen high, the list scrolling in its own box, the panel filling the height.
  const fill = useMarketsFill() && beside;
  // Rev 3: the mini portfolio under the list and the panel (from 1280px wide).
  const mini = useMarketsMiniPortfolio() && fill;
  const hasAccount = useViewedAccount().address !== null;
  const panel: ReactNode =
    openId !== null ? (
      <InlineMarket
        key={openId}
        outcome={openId}
        variant={compact ? 'narrow' : 'wide'}
        linkSearch={linkSearch}
        beside={beside}
        fill={fill}
        onClose={closePanel}
      />
    ) : null;
  const openIndex =
    openId === null
      ? -1
      : visible.findIndex((e) =>
          e.kind === 'tile'
            ? e.item.outcome === openId
            : e.group.rows.some((r) => r.item.outcome === openId) || e.group.fallback?.item.outcome === openId,
        );
  const tiles: ReactNode[] = visible.map((e) =>
    e.kind === 'tile' ? (
      <OutcomeTile
        key={e.item.outcome}
        item={e.item}
        linkSearch={linkSearch}
        selected={e.item.outcome === openId}
        onSelect={openPanel}
      />
    ) : (
      <QuestionCard
        key={`q${e.group.question}`}
        group={e.group}
        linkSearch={linkSearch}
        sort={sort}
        selected={openId}
        onSelect={openPanel}
      />
    ),
  );
  if (panel !== null && !compact) {
    tiles.splice(
      panelSlot(openIndex, tileCols, tiles.length),
      0,
      <li key="panel" className="col-span-full">
        {panel}
      </li>,
    );
  }
  const moreButton =
    entries.length > visible.length ? (
      <div className="flex justify-center">
        <button
          type="button"
          className="rounded-lg border border-border px-4 py-2 text-sm hover:bg-surface-2"
          onClick={() => setPage({ key: filterKey, count: count + PAGE_SIZE })}
        >
          {t('catalog.show_more', { n: entries.length - visible.length })}
        </button>
      </div>
    ) : null;

  // The loaded catalog: controls, then the list, with the market panel beside it in compact mode on wide screens.
  const catalog = ((): ReactNode => {
    const besideOpen = compact && beside && panel !== null && items.length > 0 && filtered.length > 0;
    const controls = (
      <>
        <CatalogControls
          query={query}
          onQuery={setQuery}
          sort={sort}
          onSort={setSort}
          venues={venues}
          selected={selected}
          onToggleVenue={toggleVenue}
          onClearVenues={() => setVenues([])}
        />
        <TagChips selected={row.selected} offered={row.offered} onChange={setTags} />
        {/* View and layout are in the header Settings menu (#2242). */}
      </>
    );
    const list =
      items.length === 0 ? (
        <div className="card p-6 text-sm text-muted">
          {scopeVenue !== null
            ? t('catalog.empty_venue', { network, venue: venueLabel(scopeVenue) })
            : t('catalog.empty', { network })}
        </div>
      ) : filtered.length === 0 ? (
        <div className="card space-y-3 p-6 text-sm text-muted">
          <p>{t('catalog.no_match')}</p>
          <button type="button" className="text-accent hover:underline" onClick={clearFilters}>
            {t('catalog.clear_filters')}
          </button>
        </div>
      ) : compact ? (
        <>
          {/* An outcome the list does not show (filtered out, further down the pages) still has its panel. */}
          {panel && !beside && openIndex < 0 ? panel : null}
          <CompactList
            entries={visible}
            linkSearch={linkSearch}
            sort={sort}
            selected={openId}
            onSelect={openPanel}
            panel={beside ? null : panel}
            dense={besideOpen}
          />
          {moreButton}
        </>
      ) : (
        <>
          <ul className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3" aria-label={t('catalog.list')} data-tour="catalog-list">
            {tiles}
          </ul>
          {moreButton}
        </>
      );
    if (fill) {
      // #2242: one screen high. The list scrolls in its own box under the search box and chips; the panel beside it
      // has a constant size. Rev 3: the search box and the filter chips are always as wide as they are while the panel
      // is open (--panel-w is the panel's width), so opening, closing or switching a market never re-wraps them and the
      // list does not move under the pointer. With an account the mini portfolio takes the bottom of the screen and the
      // list + panel row is MARKETS_TOP_HEIGHT high (the panel ends just below the book and the ticket); without one it
      // is a single line and the row takes the rest.
      return (
        <div
          className="flex min-h-0 flex-1 flex-col gap-3 [--panel-w:28rem] xl:[--panel-w:44rem] 2xl:[--panel-w:48rem]"
          data-testid="catalog-fill"
        >
          <div
            className={`flex min-h-0 gap-4 ${mini && hasAccount ? 'flex-none' : 'flex-1'}`}
            style={mini && hasAccount ? { height: MARKETS_TOP_HEIGHT } : undefined}
            data-testid="catalog-top"
          >
            <div className="flex min-h-0 min-w-0 flex-1 flex-col gap-3">
              <div
                className={`flex flex-col gap-3 ${besideOpen ? '' : 'max-w-[calc(100%_-_var(--panel-w)_-_1rem)]'}`}
                data-testid="catalog-controls-box"
              >
                {controls}
              </div>
              <div className="-mx-1 min-h-0 flex-1 space-y-4 overflow-y-auto px-1 pb-1" data-testid="catalog-scroll">
                {list}
              </div>
            </div>
            {besideOpen ? (
              <div className="flex min-h-0 w-[var(--panel-w)] shrink-0 flex-col" data-testid="panel-column">
                {panel}
              </div>
            ) : null}
          </div>
          {mini ? <MiniPortfolio className={hasAccount ? 'min-h-0 flex-1' : 'flex-none'} /> : null}
        </div>
      );
    }
    return (
      <div className="space-y-4">
        {controls}
        {list}
      </div>
    );
  })();

  return (
    <section ref={sectionRef} className={fill ? 'flex min-h-0 flex-1 flex-col' : undefined} data-tour="catalog">
      {/* #2242: the heading and the count line are for screen readers only, so the list starts higher on the page. */}
      <div className="sr-only">
        <h1>{t('nav.markets')}</h1>
        {outcomes ? (
          <p data-testid="catalog-count">
            {filtering ? (
              <Msg
                k="catalog.count_filtered"
                args={{
                  shown: <span className="num text-fg">{filtered.length}</span>,
                  n: items.length,
                  count: <span className="num">{items.length}</span>,
                  network,
                }}
              />
            ) : (
              <Msg k="catalog.count" args={{ n: items.length, count: <span className="num text-fg">{items.length}</span>, network }} />
            )}
            {view === 'grouped' && questionCount > 0 ? (
              <span data-testid="catalog-questions">
                {' · '}
                <Msg k="catalog.count_questions" args={{ n: questionCount, count: <span className="num">{questionCount}</span> }} />
              </span>
            ) : null}
            {scopeVenue !== null && (
              <span data-testid="catalog-venue">
                {' · '}
                <Msg k="venue.current" args={{ venue: <span className="font-medium text-fg">{venueLabel(scopeVenue)}</span> }} />
              </span>
            )}
            {mids ? null : ` · ${t('catalog.prices_loading')}`}
          </p>
        ) : null}
      </div>

      {meta.isPending ? (
        <div className="card p-6 text-sm text-muted" role="status">
          {t('catalog.loading', { network })}
        </div>
      ) : !outcomes ? (
        <div className="card space-y-3 p-6 text-sm" role="alert">
          <p className="text-danger">{t('catalog.load_error', { network })}</p>
          <p className="break-all text-muted">
            {meta.error instanceof Error ? meta.error.message : String(meta.error ?? t('catalog.unknown_error'))}
          </p>
          <button
            type="button"
            className="rounded-lg border border-border px-3 py-1.5 hover:bg-surface-2"
            onClick={() => void meta.refetch()}
          >
            {t('common.retry')}
          </button>
        </div>
      ) : (
        catalog
      )}
    </section>
  );
}
